/**
 * Range-mode location section: month-relative week columns for one selected
 * metric, followed by the whole-range totals and the unchanged forward outlook.
 *
 * Column layout, N = periods.length (2-16):
 *   1        Team / Salesperson  (sticky)
 *   2..N+1   one column per week bucket, selected metric
 *   N+2..5   Range Total: Leads, Visits, Appts, Sales
 *   N+6..8   Conversion: Visits/Lead, Sales/Visit, Sales/Lead
 *   N+9      Active Leads (as of today)
 *   N+10,11  Next 3, Next 5
 *
 * The table is `min-w-full w-auto`, NOT `w-full`: with `w-full` the browser
 * compresses columns to fit the container instead of overflowing, so
 * `overflow-x-auto` never engages and the numbers wrap into an unreadable mess.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { SalesFunnelRow } from '../../../../services/api';
import {
  FunnelMetricKey,
  FunnelPeriod,
  SalesTeamLocationSection,
  SalesTeamSectionP,
} from '../../../../types/salesTeamFunnel';
import { EmptyTeamRow, shortDate } from '../../../shared/SalesFunnelRows';
import { funnelRowId, windowSummary } from './FunnelLocationSection';
import {
  WeekMemberRow,
  WeekTeamHeaderRow,
  WeekTotalsRow,
} from './WeekFunnelRows';
import {
  isInactiveRow,
  needsYearSuffix,
  periodLabel,
  weekColCount,
} from './funnelWeekHelpers';

const STICKY_HEAD = 'sticky left-0 z-20 bg-gray-50 shadow-[inset_-1px_0_0_0_rgb(229,231,235)]';
const PRINT_OPTIONAL = 'funnel-print-optional';

/**
 * Scroll controls for the week table.
 *
 * The container has always been scrollable — that was measured in headless
 * Chromium against the compiled CSS. What operators lacked was any way to work
 * it: Chrome hides overlay scrollbars until a scroll is already in progress,
 * and a mouse wheel does nothing over a horizontal-only scroller (that needs
 * Shift+wheel or a trackpad swipe). So a wide report looked truncated and
 * un-scrollable. These buttons make it explicit and mouse-friendly; the region
 * is also focusable so the arrow keys work.
 */
function useScrollControls() {
  const ref = useRef<HTMLDivElement>(null);
  const [state, setState] = useState({ canScroll: false, atStart: true, atEnd: false });

  const sync = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const max = el.scrollWidth - el.clientWidth;
    setState({
      canScroll: max > 1,
      atStart: el.scrollLeft <= 1,
      atEnd: el.scrollLeft >= max - 1,
    });
  }, []);

  useEffect(() => {
    sync();
    const el = ref.current;
    if (!el) return;
    el.addEventListener('scroll', sync, { passive: true });
    window.addEventListener('resize', sync);
    return () => {
      el.removeEventListener('scroll', sync);
      window.removeEventListener('resize', sync);
    };
    // Re-measure when the column count or metric changes the table's width.
  }, [sync]);

  // A page is most of the visible width, so consecutive clicks keep a little
  // overlap for context rather than jumping blind.
  const nudge = useCallback((dir: -1 | 1) => {
    const el = ref.current;
    if (!el) return;
    el.scrollBy({ left: dir * Math.max(240, el.clientWidth * 0.8), behavior: 'smooth' });
  }, []);

  return { ref, ...state, nudge, sync };
}

const ScrollButtons: React.FC<{
  atStart: boolean;
  atEnd: boolean;
  onNudge: (dir: -1 | 1) => void;
  label: string;
}> = ({ atStart, atEnd, onNudge, label }) => {
  const base = 'p-1 rounded border border-gray-300 text-gray-600 transition-colors';
  const on = 'hover:bg-gray-100 hover:text-gray-900';
  const off = 'opacity-40 cursor-not-allowed';
  return (
    <span className="inline-flex items-center gap-1 print:hidden">
      <button
        type="button"
        onClick={() => onNudge(-1)}
        disabled={atStart}
        aria-label={`Scroll ${label} left`}
        className={`${base} ${atStart ? off : on}`}
      >
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
      <button
        type="button"
        onClick={() => onNudge(1)}
        disabled={atEnd}
        aria-label={`Scroll ${label} right`}
        className={`${base} ${atEnd ? off : on}`}
      >
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </span>
  );
};

export const FunnelWeekLocationSection: React.FC<{
  location: SalesTeamLocationSection;
  periods: FunnelPeriod[];
  metric: FunnelMetricKey;
  metricLabel: string;
  rangeStartDate: string;
  rangeEndDate: string;
  openRows: Set<string>;
  onToggle: (rowId: string) => void;
  activeLeadsUnavailable: boolean;
  reportDateIsToday: boolean;
  hideInactive: boolean;
}> = ({
  location, periods, metric, metricLabel, rangeStartDate, rangeEndDate,
  openRows, onToggle, activeLeadsUnavailable, reportDateIsToday, hideInactive,
}) => {
  const colCount = weekColCount(periods.length);
  const withYear = needsYearSuffix(periods);
  const { ref: scrollRef, canScroll, atStart, atEnd, nudge, sync } = useScrollControls();
  // Re-measure when anything that changes the table's width changes. `sync` is
  // useCallback-stable; the hook's return object is NOT (it is rebuilt every
  // render), so destructure rather than depending on the object — listing it
  // here would re-run this effect on every render it triggers, forever.
  useEffect(() => { sync(); }, [periods.length, metric, hideInactive, sync]);
  const summary = windowSummary(
    location.pastDates,
    location.nextDates,
    { startDate: rangeStartDate, endDate: rangeEndDate },
  );
  const rowId = (team: SalesTeamSectionP, row: SalesFunnelRow) =>
    funnelRowId(location.locationId, team, row);

  return (
    <div className="mb-8 break-inside-avoid funnel-location-block" style={{ pageBreakInside: 'avoid' }}>
      <div className="flex items-baseline justify-between mb-2 gap-3">
        <h3 className="text-base font-semibold text-gray-900">{location.locationName}</h3>
        <span className="flex items-center gap-3">
          <span className="text-xs text-gray-500">{summary}</span>
          {canScroll && (
            <ScrollButtons
              atStart={atStart}
              atEnd={atEnd}
              onNudge={nudge}
              label={location.locationName}
            />
          )}
        </span>
      </div>

      {location.teams.length === 0 ? (
        <div className="text-sm text-gray-500 italic border border-gray-200 rounded-lg px-4 py-6 text-center">
          No funnel activity or teams at this location in this date range.
        </div>
      ) : (
        <div
          ref={scrollRef}
          // tabIndex + role make the arrow keys work and give screen readers a
          // scrollable region rather than a silently clipped table.
          tabIndex={0}
          role="region"
          aria-label={`${location.locationName} weekly funnel table, horizontally scrollable`}
          className="overflow-x-auto funnel-week-scroller border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-400"
        >
          <table className="min-w-full w-auto text-sm">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500">
              <tr className="border-b border-gray-200">
                <th
                  rowSpan={2}
                  className={`${STICKY_HEAD} px-3 py-2 text-left align-bottom min-w-[200px] max-w-[260px]`}
                >
                  Team / Salesperson
                </th>
                {/* The metric belongs in the HEADER, not only in the dropdown —
                    a printed page or a pasted screenshot has no dropdown. */}
                <th colSpan={periods.length} className="px-2 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200">
                  Weekly {metricLabel}
                </th>
                <th colSpan={4} className="px-2 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200">
                  Range Total
                  <span className="ml-1 font-normal normal-case text-gray-400">
                    ({shortDate(rangeStartDate)}–{shortDate(rangeEndDate)})
                  </span>
                </th>
                <th colSpan={3} className={`px-2 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200 ${PRINT_OPTIONAL}`}>
                  Conversion
                </th>
                <th className="px-2 py-1.5 border-l border-gray-200" />
                <th colSpan={2} className={`px-2 py-1.5 text-center font-semibold text-gray-600 border-l border-gray-200 ${PRINT_OPTIONAL}`}>
                  Appts Scheduled
                  <span className="ml-1 font-normal normal-case text-gray-400">
                    ({reportDateIsToday ? 'includes today' : 'includes end date'})
                  </span>
                </th>
              </tr>
              <tr>
                {periods.map((period, i) => (
                  <th
                    key={period.key}
                    title={period.partial
                      ? `Partial week — ${period.sublabel} (${period.days} of 7 days)`
                      : period.sublabel}
                    className={`px-2 py-2 text-right whitespace-nowrap ${i === 0 ? 'border-l border-gray-200' : ''}`}
                  >
                    {periodLabel(period, withYear)}
                    {period.partial && <span className="text-amber-500">*</span>}
                    <span className="block font-normal normal-case text-gray-400" style={{ fontSize: '10px' }}>
                      {period.sublabel}
                    </span>
                  </th>
                ))}
                <th className="px-3 py-2 text-right border-l border-gray-200">Leads</th>
                <th className="px-3 py-2 text-right">Visits</th>
                <th className="px-3 py-2 text-right">Appts</th>
                <th className="px-3 py-2 text-right">Sales</th>
                <th className={`px-3 py-2 text-right border-l border-gray-200 ${PRINT_OPTIONAL}`}>Visits/Lead</th>
                <th className={`px-3 py-2 text-right ${PRINT_OPTIONAL}`}>Sales/Visit</th>
                <th className={`px-3 py-2 text-right ${PRINT_OPTIONAL}`}>Sales/Lead</th>
                <th className="px-3 py-2 text-right border-l border-gray-200 whitespace-nowrap">
                  Active Leads
                  <span className="block font-normal normal-case text-gray-400" style={{ fontSize: '10px' }}>
                    as of today
                  </span>
                </th>
                <th className={`px-3 py-2 text-right border-l border-gray-200 ${PRINT_OPTIONAL}`}>Next 3</th>
                <th className={`px-3 py-2 text-right ${PRINT_OPTIONAL}`}>Next 5</th>
              </tr>
            </thead>
            <tbody>
              {location.teams.map((team) => {
                // Filtering is display-only — subtotals still come from the
                // backend and cover every member of the team.
                const visibleRows = hideInactive
                  ? team.rows.filter((row) => !isInactiveRow(row))
                  : team.rows;
                return (
                  <React.Fragment key={team.managerId || 'unassigned'}>
                    <WeekTeamHeaderRow
                      managerId={team.managerId}
                      managerName={team.managerName}
                      rowCount={team.rows.length}
                      colSpan={colCount}
                    />
                    {visibleRows.length === 0 ? (
                      <EmptyTeamRow
                        colSpan={colCount}
                        label={team.rows.length === 0
                          ? 'No funnel activity from this team in the date range.'
                          : 'All members of this team are hidden by the no-activity filter.'}
                      />
                    ) : (
                      visibleRows.map((row) => (
                        <WeekMemberRow
                          key={rowId(team, row)}
                          row={row}
                          rowId={rowId(team, row)}
                          isOpen={openRows.has(rowId(team, row))}
                          onToggle={onToggle}
                          periods={periods}
                          metric={metric}
                          nextDates={location.nextDates}
                          reportDate={rangeEndDate}
                          reportDateIsToday={reportDateIsToday}
                        />
                      ))
                    )}
                    <WeekTotalsRow
                      label={team.managerId ? `Team ${team.managerName} Subtotal` : 'Unassigned Subtotal'}
                      totals={team.subtotals}
                      periods={periods}
                      metric={metric}
                      activeLeadsUnavailable={activeLeadsUnavailable}
                    />
                  </React.Fragment>
                );
              })}
            </tbody>
            <tfoot>
              <WeekTotalsRow
                label={`${location.locationName} Total`}
                totals={location.totals}
                periods={periods}
                metric={metric}
                emphasis
                activeLeadsUnavailable={activeLeadsUnavailable}
              />
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
};
